import { HOME_PAGE_CATEGORY_COLOR_CLASSES } from '@/lib/help-center-home-categories';
import { cn } from '@/lib/utils';

export type GuideCardTone = {
  card: string;
  border: string;
  hoverBorder: string;
  hoverShadow: string;
  badge: string;
  watermark: string;
  titleHover: string;
  accentBar: string;
};

export const GUIDE_CARD_FALLBACK_TONE: GuideCardTone = {
  card: 'bg-gradient-to-br from-sky-50 via-white to-white',
  border: 'border-sky-100',
  hoverBorder: 'hover:border-sky-300',
  hoverShadow: 'hover:shadow-lg hover:shadow-sky-100/70',
  badge: 'bg-sky-100 text-sky-700',
  watermark: 'text-sky-100',
  titleHover: 'group-hover:text-sky-800',
  accentBar: 'bg-gradient-to-r from-sky-600 to-sky-400',
};

export const GUIDE_CARD_TONES: Record<string, GuideCardTone> = {
  indigo: {
    card: 'bg-gradient-to-br from-indigo-50 via-white to-white',
    border: 'border-indigo-100',
    hoverBorder: 'hover:border-indigo-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-indigo-100/70',
    badge: 'bg-indigo-100 text-indigo-700',
    watermark: 'text-indigo-100',
    titleHover: 'group-hover:text-indigo-900',
    accentBar: 'bg-gradient-to-r from-indigo-600 to-indigo-400',
  },
  blue: {
    card: 'bg-gradient-to-br from-blue-50 via-white to-white',
    border: 'border-blue-100',
    hoverBorder: 'hover:border-blue-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-blue-100/70',
    badge: 'bg-blue-100 text-blue-700',
    watermark: 'text-blue-100',
    titleHover: 'group-hover:text-blue-900',
    accentBar: 'bg-gradient-to-r from-blue-600 to-sky-400',
  },
  emerald: {
    card: 'bg-gradient-to-br from-emerald-50 via-white to-white',
    border: 'border-emerald-100',
    hoverBorder: 'hover:border-emerald-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-emerald-100/70',
    badge: 'bg-emerald-100 text-emerald-700',
    watermark: 'text-emerald-100',
    titleHover: 'group-hover:text-emerald-900',
    accentBar: 'bg-gradient-to-r from-emerald-600 to-emerald-400',
  },
  amber: {
    card: 'bg-gradient-to-br from-amber-50 via-white to-white',
    border: 'border-amber-100',
    hoverBorder: 'hover:border-amber-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-amber-100/70',
    badge: 'bg-amber-100 text-amber-800',
    watermark: 'text-amber-100',
    titleHover: 'group-hover:text-amber-900',
    accentBar: 'bg-gradient-to-r from-amber-500 to-amber-300',
  },
  sky: {
    card: 'bg-gradient-to-br from-sky-50 via-white to-white',
    border: 'border-sky-100',
    hoverBorder: 'hover:border-sky-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-sky-100/70',
    badge: 'bg-sky-100 text-sky-700',
    watermark: 'text-sky-100',
    titleHover: 'group-hover:text-sky-900',
    accentBar: 'bg-gradient-to-r from-sky-600 to-sky-400',
  },
  violet: {
    card: 'bg-gradient-to-br from-violet-50 via-white to-white',
    border: 'border-violet-100',
    hoverBorder: 'hover:border-violet-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-violet-100/70',
    badge: 'bg-violet-100 text-violet-700',
    watermark: 'text-violet-100',
    titleHover: 'group-hover:text-violet-900',
    accentBar: 'bg-gradient-to-r from-violet-600 to-violet-400',
  },
  rose: {
    card: 'bg-gradient-to-br from-rose-50 via-white to-white',
    border: 'border-rose-100',
    hoverBorder: 'hover:border-rose-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-rose-100/70',
    badge: 'bg-rose-100 text-rose-700',
    watermark: 'text-rose-100',
    titleHover: 'group-hover:text-rose-900',
    accentBar: 'bg-gradient-to-r from-rose-600 to-rose-400',
  },
  cyan: {
    card: 'bg-gradient-to-br from-cyan-50 via-white to-white',
    border: 'border-cyan-100',
    hoverBorder: 'hover:border-cyan-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-cyan-100/70',
    badge: 'bg-cyan-100 text-cyan-700',
    watermark: 'text-cyan-100',
    titleHover: 'group-hover:text-cyan-900',
    accentBar: 'bg-gradient-to-r from-cyan-600 to-cyan-400',
  },
  fuchsia: {
    card: 'bg-gradient-to-br from-fuchsia-50 via-white to-white',
    border: 'border-fuchsia-100',
    hoverBorder: 'hover:border-fuchsia-300',
    hoverShadow: 'hover:shadow-lg hover:shadow-fuchsia-100/70',
    badge: 'bg-fuchsia-100 text-fuchsia-700',
    watermark: 'text-fuchsia-100',
    titleHover: 'group-hover:text-fuchsia-900',
    accentBar: 'bg-gradient-to-r from-fuchsia-600 to-fuchsia-400',
  },
};

export function resolveGuideCardTone(colorKey: string | undefined | null): GuideCardTone {
  if (colorKey && GUIDE_CARD_TONES[colorKey]) return GUIDE_CARD_TONES[colorKey];
  if (colorKey && HOME_PAGE_CATEGORY_COLOR_CLASSES[colorKey]) {
    return { ...GUIDE_CARD_FALLBACK_TONE, badge: HOME_PAGE_CATEGORY_COLOR_CLASSES[colorKey].badge };
  }
  return GUIDE_CARD_FALLBACK_TONE;
}

export function guidePremiumCardClassName(tone: GuideCardTone, className?: string): string {
  return cn(
    'group relative flex h-full min-h-[158px] flex-col overflow-hidden rounded-2xl border p-5 shadow-sm md:p-6',
    'transition-all duration-300 ease-out hover:-translate-y-1',
    tone.card,
    tone.border,
    tone.hoverBorder,
    tone.hoverShadow,
    className
  );
}
